import React from 'react'
import { Link } from 'react-router-dom'
import { Mail, Phone, MapPin } from 'lucide-react'
import { Partners } from '@/components'
import { useLanguage } from '@/contexts/LanguageContext'
import deepWindLogo from '../../assets/deepwind-logo.png'

const Footer: React.FC = () => {
  const { t } = useLanguage()

  const companyLinks = [
    { name: t.footer.about, href: '/about' },
  ]

  const supportLinks = [
    { name: t.footer.contact, href: '/contact' },
    { name: t.footer.privacy, href: '/privacy' },
  ]

  return (
    <div>
      {/* Partners Section */}
      <Partners />
      
      {/* Footer */}
      <footer className="bg-secondary-900 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Mobile Footer - Compact 2 line design */}
          <div className="py-4 md:hidden">
            <div className="text-center space-y-3">
              {/* First line: Links */}
              <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs">
                <Link to="/about" className="text-secondary-300 hover:text-white">
                  {t.footer.about}
                </Link>
                <span className="text-secondary-600">•</span>
                <Link to="/contact" className="text-secondary-300 hover:text-white">
                  {t.footer.contact}
                </Link>
                <span className="text-secondary-600">•</span>
                <Link to="/privacy" className="text-secondary-300 hover:text-white">
                  {t.footer.privacy}
                </Link>
              </div>

              {/* Second line: Contact info */}
              <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-xs leading-5 text-secondary-400">
                <span className="break-all sm:break-normal">{t.footer.email}</span>
                <span className="text-secondary-600">•</span>
                <span className="hidden sm:inline">{t.footer.phone}</span>
                <span className="hidden sm:inline text-secondary-600">•</span>
                <span>{t.footer.address}</span>
              </div>
            </div>
          </div>

          {/* Desktop Footer - Full design */}
          <div className="py-12 hidden md:block">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {/* Company Info */}
              <div className="col-span-1 lg:col-span-2">
                <div className="flex items-center space-x-2 mb-6">
                  <div className="flex items-center justify-center w-8 h-8 bg-white rounded-lg p-1">
                    <img 
                      src={deepWindLogo} 
                      alt="VentraWind Logo" 
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div>
                    <span className="text-xl font-bold">VentraWind</span>
                  </div>
                </div>
                <p className="text-secondary-300 mb-6 max-w-md">
                  {t.footer.description}
                </p>
                <div className="flex items-center justify-start gap-4 text-secondary-300">
                  <span className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-primary-400" />
                    {t.footer.email}
                  </span>
                  <span className="text-secondary-600">•</span>
                  <span className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-primary-400" />
                    {t.footer.phone}
                  </span>
                  <span className="text-secondary-600">•</span>
                  <span className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-primary-400" />
                    {t.footer.address}
                  </span>
                </div>
              </div>

              {/* Company Links */}
              <div>
                <h3 className="text-lg font-semibold mb-4">{t.footer.company}</h3>
                <ul className="space-y-2">
                  {companyLinks.map((link) => (
                    <li key={link.name}>
                      <Link
                        to={link.href}
                        className="text-secondary-300 hover:text-white transition-colors duration-200"
                      >
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Support Links */}
              <div>
                <h3 className="text-lg font-semibold mb-4">{t.footer.support}</h3>
                <ul className="space-y-2">
                  {supportLinks.map((link) => (
                    <li key={link.name}>
                      <Link
                        to={link.href}
                        className="text-secondary-300 hover:text-white transition-colors duration-200"
                      >
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
export default Footer 
